import { useEffect, useState } from 'react';
import {
  getOrCreateIssueForResponse,
  updateIssueDetails,
  uploadIssueEvidence,
} from '../services/issues';
import type { InspectionCheck, InspectionResponseRecord } from '../types/inspection';
import type { IssuePriority, IssueRecord } from '../types/issues';

interface IssueCaptureProps {
  ownerId: string;
  assessmentId: string;
  response: InspectionResponseRecord;
  check: InspectionCheck;
  online: boolean;
  onDone: (issue: IssueRecord) => void;
  onBack: () => void;
}

const priorities: Array<{ value: IssuePriority; label: string }> = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'important', label: 'Important' },
  { value: 'monitor', label: 'Monitor' },
  { value: 'minor', label: 'Minor' },
];

export function IssueCapture({
  ownerId,
  assessmentId,
  response,
  check,
  online,
  onDone,
  onBack,
}: IssueCaptureProps) {
  const [issue, setIssue] = useState<IssueRecord | null>(null);
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<IssuePriority>('important');
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'saving' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadIssue() {
      try {
        const nextIssue = await getOrCreateIssueForResponse(
          ownerId,
          assessmentId,
          response,
          check,
        );

        if (!cancelled) {
          setIssue(nextIssue);
          setDescription(nextIssue.description ?? '');
          setPriority(nextIssue.priority);
          setStatus('ready');
        }
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : 'Could not create the finding.');
          setStatus('error');
        }
      }
    }

    void loadIssue();
    return () => {
      cancelled = true;
    };
  }, [assessmentId, check, ownerId, response]);

  async function saveFinding() {
    if (!issue || !online) {
      setError(!online ? 'Reconnect before saving this finding.' : 'Finding is still loading.');
      return;
    }

    setStatus('saving');
    setError(null);

    try {
      const updated = await updateIssueDetails(
        ownerId,
        issue.id,
        description,
        priority,
      );

      if (file) {
        await uploadIssueEvidence(
          ownerId,
          assessmentId,
          updated.id,
          file,
          description,
        );
      }

      setStatus('ready');
      onDone(updated);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save this finding.');
      setStatus('error');
    }
  }

  return (
    <main className="page finding-page">
      <button className="text-button" type="button" onClick={onBack}>
        ← Back to check
      </button>

      <section className="finding-card">
        <span className="eyebrow">Finding · {check.area}</span>
        <h1>{check.title}</h1>
        <p className="lead">
          Add context that will help you or the builder understand what needs follow-up. A photo is recommended when it can be captured safely.
        </p>

        {status === 'loading' ? <div className="status-banner">Preparing finding…</div> : null}
        {error ? <div className="status-banner" role="alert">{error}</div> : null}

        <label className="field">
          <span>What did you observe?</span>
          <textarea
            rows={4}
            value={description}
            placeholder="Example: Water remains pooled near the bathroom floor drain after a small pour test."
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Follow-up priority</span>
          <select
            value={priority}
            onChange={(event) => setPriority(event.target.value as IssuePriority)}
          >
            {priorities.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>

        <label className="evidence-picker">
          <span className="evidence-icon" aria-hidden="true">＋</span>
          <span>
            <strong>Add photo evidence</strong>
            <small>JPEG, PNG or WebP. Large photos are compressed before private upload.</small>
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </label>

        {file ? (
          <div className="selected-evidence">
            <strong>{file.name}</strong>
            <span>{Math.max(1, Math.round(file.size / 1024))} KB selected</span>
            <button className="text-button" type="button" onClick={() => setFile(null)}>
              Remove
            </button>
          </div>
        ) : null}

        <div className="expert-boundary">
          <strong>Evidence boundary</strong>
          <p>
            Photograph only areas you can access safely. Do not open electrical panels, dismantle fittings, climb onto unsafe surfaces or enter restricted construction zones for evidence.
          </p>
        </div>

        <button
          className="button button-primary"
          type="button"
          disabled={!issue || status === 'saving' || !online}
          onClick={() => void saveFinding()}
        >
          {status === 'saving' ? 'Saving finding…' : 'Save finding and continue'}
        </button>
      </section>
    </main>
  );
}
